import React from 'react';
import { twMerge } from 'tailwind-merge';

import ContextMenu from 'flow/components/ContextMenu';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectApplicationLanguage } from 'flow/selectors/app-selectors';
import {
    selectContact,
    selectExtensionStatus,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import { isTeamsTypeIntegration } from 'flow/utils/app-utils';
import ContactAvatar from './ContactAvatar';
import Icon from './Icon';
import IconButton from './IconButton';
import PauseProfileButton from './PauseProfileButton';
import ProfilePicker from './ProfilePicker';
import ToolbarAvatar from './ToolbarAvatar';
import ToolbarMenu from './ToolbarMenu';

type OwnProfileMenuProps = {
    className: string;
};

const OwnProfileMenu = ({ className }: OwnProfileMenuProps) => {
    const { i18next } = useAppContext();

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const ownContact = useAppSelector((state) =>
        selectContact(state, ownExtensionKey),
    );
    const ownStatus = useAppSelector((state) =>
        selectExtensionStatus(state, ownExtensionKey),
    );

    return (
        <div className={className}>
            <ContextMenu
                triggerLabel={i18next.t('toolbar.account_menu')}
                dataTestId="account-menu-button"
                className="hover:darken-0 relative flex bg-transparent p-0"
                icon={
                    <ContactAvatar
                        contact={ownContact}
                        status={ownStatus}
                        size="toolbar"
                    />
                }
                placement="bottom-end"
            >
                <ToolbarAvatar />
            </ContextMenu>
        </div>
    );
};

const AppToolbar = () => {
    const { embedded, i18next, setShowDialpad, showDialpad } = useAppContext();

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    // Make the component rerender the translations after switching language
    useAppSelector(selectApplicationLanguage);

    return (
        <header
            className={twMerge(
                '@container relative z-30 flex flex-wrap items-center gap-3 bg-gray-100 p-3 md:justify-between',
                !isTeamsTypeIntegration(embedded) ? 'border-b' : 'border-t',
            )}
        >
            {!isTeamsTypeIntegration(embedded) ? (
                <>
                    <ToolbarMenu />
                    <div className="me-auto">
                        <IconButton
                            onClick={() => setShowDialpad(!showDialpad)}
                            tooltipLabel={i18next.t(
                                'central_switch.dialpad.description',
                            )}
                            dataTestId="dialpad-button"
                            aria-label={i18next.t(
                                'central_switch.dialpad.description',
                            )}
                            className={twMerge(
                                'rounded-sm p-2',
                                showDialpad
                                    ? 'bg-main text-white'
                                    : 'bg-gray-200 text-gray-600',
                            )}
                        >
                            <Icon iconKey="dialpad" />
                        </IconButton>
                    </div>
                </>
            ) : (
                <div className="me-auto" />
            )}

            <OwnProfileMenu className="@2xl:hidden" />
            <div className="flex w-full gap-3 @[406px]:w-[calc(100%-70px)] @[440px]:w-full @2xl:!w-auto">
                <div className="ms-auto flex min-w-max basis-full gap-x-3">
                    <ProfilePicker
                        extensionKey={ownExtensionKey}
                        showSettingsLink={true}
                        enableLogInOptions={true}
                    />
                    <PauseProfileButton />
                </div>
            </div>
            <OwnProfileMenu className="hidden @2xl:block" />
        </header>
    );
};

export default AppToolbar;
